import { SECTION_NAMES } from '@repo/common';
import { TOURNAMENTS_CAMPAIGN_TYPE } from 'features/campaigns';
import type { CampaignSource, ITournament, IParsedTournament } from 'features/campaigns';
import { useAppStore } from 'store/app/appStore';
import { useTournamentCupStore } from '../../store/tournamentCupStore';
import type { ApiGameSettings } from 'types/api/api.types';
import type IGameSettings from '../../../../types/models/IGameSettings';
import type { IGroupGame } from 'shared/utils/gameParsingUtils';
import { parseGameSettings } from 'shared/utils/gameParsingUtils';
import { LEVEL_ORDER } from '../campaignHelpers';
import { TOURNAMENT_STATUS_ORDER } from '../../types/variants/tournament.types';

export const isTournamentSource = (source: CampaignSource): source is ITournament | IParsedTournament => {
  return 'type' in source && source.type === TOURNAMENTS_CAMPAIGN_TYPE;
};

/** Sort tournaments by status > joined > level > endDate */
export const sortTournaments = (tournaments: (ITournament | IParsedTournament)[]): (ITournament | IParsedTournament)[] => {
  return [...tournaments].sort((a, b) => {
    const statusA = TOURNAMENT_STATUS_ORDER[a.status] || 999;
    const statusB = TOURNAMENT_STATUS_ORDER[b.status] || 999;
    if (statusA !== statusB) return statusA - statusB;

    if (a.joined !== b.joined) return a.joined ? -1 : 1;

    const levelA = LEVEL_ORDER[a.level] || 999;
    const levelB = LEVEL_ORDER[b.level] || 999;
    if (levelA !== levelB) return levelA - levelB;

    return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
  });
};

/** Parse tournament game keys into full IGameSettings objects */
const parseTournamentGames = (
  tournamentGameKeys: string[],
  normalizedGames: { [gameKey: string]: ApiGameSettings },
  topGameKeys?: Set<string>
): { games: IGameSettings[]; gamesKeys: { [key: string]: string } } => {
  const games: IGameSettings[] = [];
  const gamesKeys: { [key: string]: string } = {};

  if (!normalizedGames || Object.keys(normalizedGames).length === 0) {
    return { games, gamesKeys };
  }

  tournamentGameKeys.forEach((gameKey, index) => {
    const mutatedGame: IGroupGame = {
      order: index,
      gameKey: gameKey,
      imageLobby: 'vertical',
      imageSection: 'vertical'
    };

    const gameData = normalizedGames[gameKey];

    if (gameData) {
      const fullGameSettings = parseGameSettings({
        mutatedGame,
        gameData,
        forTournament: true,
        isTopGame: topGameKeys?.has(gameKey) ?? false
      });

      if (fullGameSettings) {
        games.push(fullGameSettings);
        gamesKeys[gameKey] = gameKey;
      }
    }
  });

  return { games, gamesKeys };
};

/** Parse raw tournaments: resolve game keys to IGameSettings objects */
export const parseTournamentsData = (
  tournaments: (ITournament | IParsedTournament)[]
): (ITournament | IParsedTournament)[] => {
  const normalizedGames = useAppStore.getState().supportedGames;

  // If games aren't loaded yet, return as-is (will be re-parsed later)
  if (!normalizedGames || Object.keys(normalizedGames).length === 0) {
    return tournaments;
  }

  const topSection = useAppStore.getState().sections.find(s => s.name === SECTION_NAMES.TOP);
  const topGameKeys = new Set(topSection?.games.map(g => g.gameKey) ?? []);

  return tournaments.map(tournament => {
    // Already parsed
    if ((tournament as IParsedTournament).gamesKeys) {
      return tournament as IParsedTournament;
    }

    const rawTournament = tournament as ITournament;
    if (rawTournament.games && Array.isArray(rawTournament.games) &&
        typeof rawTournament.games[0] === 'string') {
      const { games, gamesKeys } = parseTournamentGames(
        rawTournament.games as string[],
        normalizedGames,
        topGameKeys
      );
      return { ...rawTournament, games, gamesKeys } as IParsedTournament;
    }

    return tournament;
  });
};

/** Apply isInTournament flags to the normalized games map for ACTIVE/PROMO tournaments */
const applyTournamentFlagsToGames = (
  normalizedGames: { [gameKey: string]: ApiGameSettings },
  activeTournaments: { games: string[]; status: string }[]
): { [gameKey: string]: ApiGameSettings } => {
  const updatedGames = { ...normalizedGames };

  // Reset all isInTournament flags
  Object.keys(updatedGames).forEach(gameKey => {
    updatedGames[gameKey] = {
      ...updatedGames[gameKey],
      isInTournament: false
    };
  });

  // Set isInTournament flag for games in ACTIVE or PROMO tournaments
  activeTournaments.forEach(tournament => {
    if (tournament.status === 'ACTIVE' || tournament.status === 'PROMO') {
      tournament.games.forEach(gameKey => {
        if (updatedGames[gameKey]) {
          updatedGames[gameKey] = {
            ...updatedGames[gameKey],
            isInTournament: true
          };
        }
      });
    }
  });

  return updatedGames;
};

/** Update game flags and cup store based on active tournaments */
export const updateTournamentGameFlags = (tournaments: (ITournament | IParsedTournament)[]) => {
  const normalizedGames = useAppStore.getState().supportedGames;
  if (!normalizedGames || Object.keys(normalizedGames).length === 0) return;

  const activeTournaments = tournaments
    .filter(t => t.status === 'ACTIVE' || t.status === 'PROMO')
    .map(t => ({
      games: Array.isArray(t.games) && typeof t.games[0] === 'string'
        ? t.games as string[]
        : (t as IParsedTournament).gamesKeys
          ? Object.keys((t as IParsedTournament).gamesKeys)
          : [],
      status: t.status
    }));

  const gamesWithTournamentFlags = applyTournamentFlagsToGames(normalizedGames, activeTournaments);
  useAppStore.getState().setSupportedGames(gamesWithTournamentFlags);

  const gameKeysWithCups = Object.keys(gamesWithTournamentFlags)
    .filter(gameKey => gamesWithTournamentFlags[gameKey].isInTournament);
  useTournamentCupStore.getState().setGamesWithCups(gameKeysWithCups);
};
